import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AxiosError } from 'axios';
import MessagesBaseScreen from './MessagesBaseScreen';
import CommunicationItem from './CommunicationItem';
import { useAppDispatch, useAppSelector } from 'src/app/services/store';
import { getCurrentChild } from 'src/shared/redux-state/src';
import { getCommunicationCurrentPage, getCommunicationLastPage, getCommunicationList, getCommunicationLoading, getCommunicationPaginatedLoading } from 'src/shared/redux-state/src/lib/messages/communication/CommunicationSelectors';
import { ChildrenStoreData, CommonMessageListItemType, CommunicationResponseItem } from 'src/shared/types/src';
import { fetchCommunications } from 'src/shared/redux-state/src/lib/messages/communication/CommunicationHandler';
import { unwrapResult } from '@reduxjs/toolkit';
import LocalImages from "./../../static";
import { NotificationListContentLoader } from 'src/app/components';
import { Spinner } from 'reactstrap';
import { toast } from 'react-toastify';
import { APP_MESSAGES } from 'src/shared/config/src/lib/constants';
import { transformCommunicationItemToCommonMessageListItem } from 'src/shared/utils/src';
import { useLocation } from 'react-router-dom';

const NoMessages = () => {
    return (
        <div className='no-data-found message'>
            <img src={LocalImages.noMessageFound} alt="no-message" />
            <p className='text-center fs-16 fw-bold text-black mg-b-0 mg-t-10'>
                No Messages
            </p>
        </div>
    );
}


const CommunicationsContainer: React.FC = () => {
    const dispatch = useAppDispatch();
    const location = useLocation();

    const lastItemRef = useRef<HTMLDivElement | null>(null);
    const observer = useRef<IntersectionObserver | null>(null);
    const currentChild: ChildrenStoreData = useAppSelector(getCurrentChild);
    const childIntsanceKeyRef = useRef<string>(currentChild?.childInstanceKey);
    const communicationLoading = useAppSelector(getCommunicationLoading);
    const paginatedLoading = useAppSelector(getCommunicationPaginatedLoading);
    const communicationList = useAppSelector(getCommunicationList);
    const currentPage = useAppSelector(getCommunicationCurrentPage);
    const lastPage = useAppSelector(getCommunicationLastPage);

    const [fetchNext, setFetchNext] = useState<boolean>(true);
    const [isApiError, setApiError] = useState(false);
    const [apiErrorMsg, setApiErrorMsg] = useState(
        APP_MESSAGES.generalErrorMessage
    );

    // Intersection Observer to trigger fetchNextPage
    useEffect(() => {
        if (lastItemRef.current) {
            const options = {
                root: null, // Use the viewport as the root
                rootMargin: '0px',
                threshold: 0.1, // Trigger when 10% of the element is visible
            };

            observer.current = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        fetchNextPage();
                    }
                });
            }, options);

            observer.current?.observe(lastItemRef.current);
        }
        const currentRef = lastItemRef.current; // Capture the current ref value
        const handleObserve = () => {
            if (observer?.current && currentRef) {
                observer.current.unobserve(currentRef); // Use captured ref value in cleanup
            }
        };

        return handleObserve;
    }, [communicationList]);

    useEffect(() => {
        if (isApiError) {
            toast.error(apiErrorMsg, {
                onClose: () => setApiError(false)
            });
        }

    }, [apiErrorMsg, isApiError]);

    const fetchCommunicationsData = useCallback(async (page: number) => {
        if (!page) page = 1;
        await dispatch(fetchCommunications({ childInstanceUrl: currentChild.instanceUrl, eymanageNurseryId: currentChild.nursery.eymanNurseryId, page }))
            .then(unwrapResult)
            .catch((error) => {
                handleAPIError(error);
            });
    }, [currentChild.childInstanceKey, dispatch]);

    const handleAPIError = (error: any) => {
        if (error.name === 'AxiosError') {
            const apiError = error as AxiosError;
            if (apiError && apiError.response) {
                const { errors } = error.response.data;
                setApiError(true);
                setApiErrorMsg(
                    errors ? errors.join(', ') : APP_MESSAGES.generalErrorMessage
                );
            } else {
                setApiError(true);
                setApiErrorMsg(APP_MESSAGES.generalErrorMessage);
            }
        } else {
            setApiError(true);
            setApiErrorMsg(APP_MESSAGES.generalErrorMessage);
        }
    };


    const fetchNextPage = useCallback(async () => {
        if (fetchNext) {
            if (communicationLoading || paginatedLoading) return;
            if ((currentPage === lastPage) || paginatedLoading) {
                setFetchNext(false);
                return;
            }
            await fetchCommunicationsData(currentPage + 1);
        }
    }, [currentPage, fetchNext, lastPage, communicationLoading, paginatedLoading]);

    const init = async () => {
        fetchCommunicationsData(1);
    };

    useEffect(() => {
    //    if(!communicationList?.length || location.state?.refresh || childIntsanceKeyRef.current !== currentChild?.childInstanceKey) {
           init();
    //    }
    }, []);

    useEffect(() => {
        if(childIntsanceKeyRef.current !== currentChild?.childInstanceKey) {
            childIntsanceKeyRef.current = currentChild?.childInstanceKey;
            init();
        }
    }, [currentChild.childInstanceKey]);

    const renderFooterLoader = useCallback(() => {
        if (paginatedLoading && currentPage !== lastPage)
            return (
                <div className='d-flex flex-column align-items-center justify-content-center mg-t-20'>
                    <Spinner style={{ width: '20px', height: '20px' }} />
                </div>
            );
        return null;
    }, [paginatedLoading, currentPage, lastPage]);

    const communicationTransformedList = useMemo(() => {
        return communicationList.map((item: CommunicationResponseItem) => {
            return transformCommunicationItemToCommonMessageListItem(item);
        })
    }, [communicationList]);

    return (
        <MessagesBaseScreen showFilter={false}>
            <div className='message-list' tabIndex={0}>
                <>
                    {communicationLoading ? (
                        <div className='mg-t-15'><NotificationListContentLoader /></div>
                    ) : (
                        communicationList.length > 0 ? (
                            <>

                                {communicationTransformedList.map((item: CommonMessageListItemType, index: number) => {
                                    return (
                                        <CommunicationItem message={item}
                                            ref={index === communicationList.length - 1 ? lastItemRef : null} />
                                    )
                                })}
                                {renderFooterLoader()}
                            </>
                        ) : <NoMessages />
                    )}
                </>
            </div>
        </MessagesBaseScreen>
    )
};

export default CommunicationsContainer;